/// <reference path="../../apps/typings/navigator.d.ts"/>
/// <reference path="../models/generated/ModelsPdfStorageResponseModelApi.d.ts"/>
/// <reference path="../models/generated/BankOperationsBankOperationReturnSepaMandatApi.d.ts"/>
/// <reference path="../models/generated/BankOperationsBankOperationReturnCheckApi.d.ts"/>
/// <reference path="../models/generated/ZahlungsweisenAuthorizeHashModelApi.d.ts"/>


import { AppUtils } from '../../shared/utils/index';
import { SparteType } from '../models/api/SparteType';
import { BasicApplicationUrlBuilder } from './BasicApplicationUrlBuilder';
import { BankdatenModel } from '../models/view/Bankdaten';
import { User } from '../models/api/User/User';
import { BankApi } from '../models/generated/PersonBankApi';
import { AbweichenderKontoInhaberApi } from '../models/generated/PersonAbweichenderKontoInhaberApi';
import { PreAuthorizeRequestModelApi } from '../models/generated/ZahlungsweisenPreAuthorizeRequestModelApi';

import { AddressApi } from '../models/generated/PersonAddressApi';
import { BasicPersistenceService } from './BasicPersistenceService';
import { GETbankoperationsepamandatApi } from '../models/generated/GETbankoperationsepamandatApi';


export class BankAccountService {
	private static continueEnclosure = false;

	private readonly options: BankingAppOptions;
	private readonly applicationUrlBuilder: BasicApplicationUrlBuilder<BankingAppDefinition>;
	private readonly $ctx: JQuery;
	private readonly user: User;

	constructor(options: BankingAppOptions, user: User) {
		this.options = options;
		this.$ctx = $(this.options.$ctx);
		this.applicationUrlBuilder = new BasicApplicationUrlBuilder(options);
		this.user = user;
	}

	/**
	 * Compares the account data of the user with the entered account data.
	 * Only works, if the user is logged in.
	 *
	 * @param iban - the entered account data
	 * @param bic - the entered bic (optional)
	 * @param sparte - Sparte to perform the check for
	 * @param successCallback - Called when the compare was successful and the submit can continue.
	 * @param cancelCallback - Called when the account hint lightbox was shown, but canceled by the user.
	 * @param errorCallback - Called when a technical error occurred calling the api.
	 */
	public compare(iban: string,
		bic: string,
		sparte: SparteType,
		successCallback: () => void,
		cancelCallback: () => void,
		errorCallback: (jqxhr: JQueryXHR) => void): void {
		T.Utils.Auth.getBearerToken((token: string) => {
			AppUtils.Ajax.fragment({
				url: this.applicationUrlBuilder.fullUrl(this.options.app.apiaccountcompare),
				data: {
					Iban: iban,
					Bic: bic,
					BankEinzugMitglied: SparteType.SPARTE_MITGLIED === sparte,
					BankEinzugSchutzbrief: SparteType.SPARTE_SCHUTZBRIEF === sparte
				},
				headers: {
					Authorization: `Bearer ${token}`
				},
				timeout: 30000
			}, (data: ApiResponse<boolean>) => {
				if (data.Data) {
					successCallback();
				} else {
					this.showLightbox(successCallback, cancelCallback);
				}
			}, errorCallback);
		});
	}

	/**
	 * Render a PDF SEPA mandate to given "anchorElement" as data-uri (or blob for IE)
	 *
	 * @param token - token given by "/Help/Api/POST-api-Mitgliedschaft" response
	 * @param sparte - Sparte to create the sepa mandat for
	 * @param anchorElement - jQuery element object
	 * @param filename - optional filename for download, defaults to SepaMandat.pdf
	 * @param timeout - optional timeout in milliseconds, defaults to 30000
	 */
	public renderMandate(mglToken: string, sparte: SparteType, anchorElement: JQuery, filename: string, timeout?: number, successCallback?: () => void): void {
		const sepaRequest = new GETbankoperationsepamandatApi({
			BankEinzugMitglied: !sparte || SparteType.SPARTE_MITGLIED === sparte,
			BankEinzugSchutzbrief: !sparte || SparteType.SPARTE_SCHUTZBRIEF === sparte
		});

		const options: JQuery.AjaxSettings = {
			url: this.applicationUrlBuilder.fullUrl(this.options.app.apisepa),
			timeout: timeout || 30000,
		};

		const sepacall = (): void => {
			options.data = sepaRequest.toRequestObject();

			AppUtils.Ajax.fragment(options, (successdata: ApiResponse<BankOperationReturnSepaMandatApi>) => {
				AppUtils.View.renderPdfFileLink(anchorElement, filename, successdata.Data.SepaMandat);
				if (successCallback) {
					successCallback();
				}
			}, (jqxhr, textStatus, errorThrown) => {
				this.handleSepaApiError(jqxhr, anchorElement, errorThrown);
			});
		};

		if (!AppUtils.Helper.isEmptyString(mglToken)) {
			sepaRequest.Token = mglToken;
			sepacall();
		} else if (this.user.Valid) {
			T.Utils.Auth.getBearerToken((token: string) => {
				options.headers = { Authorization: `Bearer ${token}` };
				sepacall();
			});
		}
	}

	public getCreditCardPreAuthRequestData(betrag: number, successCallback: (data: AuthorizeHashModelApi) => void,
		errorCallback: (jqxhr: JQuery.jqXHR, textStatus?: JQuery.Ajax.ErrorTextStatus, errorThrown?: string) => void): void {
		const betragCent = parseFloat((betrag * 100).toFixed(0));
		const request = new PreAuthorizeRequestModelApi({
			Betrag: betragCent.toString(),
			Backurl: AppUtils.Url.updateUrlParameter({ SecureStatus: 'cancel' }, window.location.href),
			Errorurl: AppUtils.Url.updateUrlParameter({ SecureStatus: 'error' }, window.location.href),
			Successurl: AppUtils.Url.updateUrlParameter({ SecureStatus: 'success' }, window.location.href)
		});

		AppUtils.Ajax.fragment({
			method: 'POST',
			url: this.applicationUrlBuilder.fullUrl(this.options.app.apicreditcardpreauth),
			data: request.toRequestObject(),
			contentType: "application/x-www-form-urlencoded;charset=utf-8"
		}, (data: ApiResponse<AuthorizeHashModelApi>) => successCallback(data.Data), errorCallback);

	}

	private handleSepaApiError(jqxhr: JQuery.jqXHR, anchorElement: JQuery, errorThrown?: string): void {
		const error = this.options.app.apierrors ? this.options.app.apierrors.apisepa : errorThrown;
		let selectorPfx = "ll";
		if (anchorElement.parents('.oo-row').length) {
			selectorPfx = "oo";
		}
		const $row = anchorElement.parents(`.${selectorPfx}-row`);
		$row.addClass('is-error');
		const $col = $row.find(`.${selectorPfx}-col-data`);
		if ($col.length) {
			let $msg = $col.find(`${selectorPfx}-error-msg`);
			if (!$msg.length) {
				$msg = $(`<div class="${selectorPfx}-error-msg"></div>`).prependTo($col);
			}
			$msg.text(error);
		}
		AppUtils.Helper.logError(jqxhr, this.options.name, window.location.href, 'renderMandate');
	}

	/**
	 * Gets the account data as BankdatenModel that is stored by the iban application.
	 *
	 */
	public getBankdaten(scope = "", returnEmtpyModel = true): BankdatenModel {
		const ibanApplication = T.Utils.Store.get(BasicPersistenceService.DEFAULT_IBAN_SESSION_KEYPREFIX + scope, T.Utils.Store.SESSION);
		if (ibanApplication) {
			return BankdatenModel.createFromObject(ibanApplication.bankdatenModel);
		}
		return returnEmtpyModel ? new BankdatenModel() : null as any;
	}

	/**
	 * Gets the account data as BankdatenApi that is stored by the iban application.
	 */
	public getBankApi(scope = ""): BankApi {
		const tempModel = this.getBankdaten(scope, false);
		if (tempModel) {
			const model = new BankApi();
			model.Bankname = tempModel.Bankname;
			model.Bic = tempModel.Bic;
			model.HatAbwKontoInhaber = tempModel.HatAbweichendenKontoInhaber;
			model.Iban = tempModel.Iban;
			model.MandateReference = tempModel.MandateReference;
			model.BankEinzugMitglied = tempModel.BankEinzugMitglied;
			model.BankEinzugSchutzbrief = tempModel.BankEinzugSchutzbrief;
			if (tempModel.HatAbweichendenKontoInhaber && tempModel.AbwKontoInhaber) {
				model.AbwKontoInhaber = new AbweichenderKontoInhaberApi();
				model.AbwKontoInhaber.Vorname = tempModel.AbwKontoInhaber.Vorname;
				model.AbwKontoInhaber.Name = tempModel.AbwKontoInhaber.Name;
				model.AbwKontoInhaber.AnredeKennzeichen = tempModel.AbwKontoInhaber.AnredeKennzeichen;
				if (tempModel.AbwKontoInhaber.Adresse) {
					model.AbwKontoInhaber.Adresse = new AddressApi();
					model.AbwKontoInhaber.Titel = tempModel.AbwKontoInhaber.Titel;
					model.AbwKontoInhaber.Adresse.Auslandsadresse = tempModel.AbwKontoInhaber.Adresse.Auslandsadresse;
					model.AbwKontoInhaber.Adresse.Ergaenzungszeile = tempModel.AbwKontoInhaber.Adresse.Ergaenzungszeile;
					model.AbwKontoInhaber.Adresse.Landkennzeichen = tempModel.AbwKontoInhaber.Adresse.Landkennzeichen;
					model.AbwKontoInhaber.Adresse.Ort = tempModel.AbwKontoInhaber.Adresse.Ort;
					model.AbwKontoInhaber.Adresse.Postleitzahl = tempModel.AbwKontoInhaber.Adresse.Postleitzahl;
					model.AbwKontoInhaber.Adresse.RegionalclubCode = tempModel.AbwKontoInhaber.Adresse.RegionalclubCode;
					model.AbwKontoInhaber.Adresse.RegionalclubKennung = tempModel.AbwKontoInhaber.Adresse.RegionalclubKennung;
					model.AbwKontoInhaber.Adresse.StrasseHausnummer = tempModel.AbwKontoInhaber.Adresse.StrasseHausnummer;
				}
			}
			return model;
		}
		return new BankApi();
	}

	private showLightbox(successCallback: () => void, cancelCallback: () => void): void {
		const $closeAccountCompare = this.$ctx.find('.js-close-account-compare');

		$closeAccountCompare.on('click', /* istanbul ignore next: not waitable */(ev: JQuery.Event) => {
			ev.preventDefault();
			$closeAccountCompare.off('click');
			BankAccountService.continueEnclosure = true;
			$.magnificPopup.close();
		});


		$.magnificPopup.open({
			fixedContentPos: true,
			closeOnBgClick: false,
			enableEscapeKey: false,
			callbacks: {
				open: function () {
					$('.mfp-wrap').addClass("l-lightbox");
				},
				beforeOpen: function () {
					// setup classes
					$('#adac_account_compare_tpl').addClass("ll-lightbox-inner mfp-with-anim");
				},
				close: function () {
					if (BankAccountService.continueEnclosure) {
						successCallback();
					} else {
						cancelCallback();
					}
				}
			},
			items: {
				src: '#adac_account_compare_tpl'
			},
			type: 'inline'
		});
	}


	public checkIban(iban: string, bic: string, successCallback: (data?: ApiResponse<BankOperationReturnCheckApi>) => void, errorCallback: (jqxhr?: JQuery.jqXHR) => void): void {
		const url = this.applicationUrlBuilder.fullUrl(this.options.app.apiibancheck);
		AppUtils.Ajax.fragment({
			method: 'GET',
			url: url,
			data: {
				Iban: iban,
				Bic: (bic || "")
			}
		}, successCallback, errorCallback);
	}


	public checkIbanWithBic(iban: string, bic: string, successCallback: (data?: ApiResponse<BankOperationReturnCheckApi>) => void, errorCallback: (jqxhr?: JQuery.jqXHR) => void): void {
		const url = this.applicationUrlBuilder.fullApimUrl(this.options.app.apicheckibanwithbic);
		AppUtils.Ajax.fragment({
			method: 'GET',
			url: url,
			headers: {
				"Ocp-Apim-Subscription-Key": T.Utils.Application.apimSubscriptionKey,
			},
			data: {
				'Iban': iban,
				'BIC': bic
			}
		}, successCallback, errorCallback);
	}
}
